import { useState } from 'react';
import { Home, ApiResponse, ThreadResponse } from '../types/homes';
import { useNavigate } from 'react-router-dom';

export function useHomes() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [homes, setHomes] = useState<Home[]>([]);
  const [deletingHomeIds, setDeletingHomeIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Handle unauthorized responses
  const handleUnauthorizedResponse = (response: Response) => {
    if (response.status === 401) {
      // Redirect to login page
      navigate('/login');
      return true;
    }
    return false;
  };

  const fetchHomes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch('/api/homes/list');
      
      if (handleUnauthorizedResponse(response)) return;
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: ApiResponse = await response.json();
      if (data.success) {
        const homesData = data.threads.map((thread: ThreadResponse) => ({
          id: thread.home_id,
          threadId: thread.thread_id,
          name: thread.chat_name,
          details: thread.details,
          primary_image: thread.primary_image,
          primary_image_url: thread.primary_image_url,
          tasks_count: thread.tasks_count,
          completed_tasks_count: thread.completed_tasks_count
        }));
        setHomes(homesData);
      } else {
        throw new Error(data.error || 'Failed to fetch homes');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load homes');
      console.error('Error fetching homes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createHome = async (
    homeName: string, 
    address: string, 
    details: {
      bedrooms: number;
      bathrooms: number;
      sqft: number;
      price: number;
    }
  ) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/homes/new', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name: homeName,
          details: {
            address,
            bedrooms: details.bedrooms,
            bathrooms: details.bathrooms,
            sqft: details.sqft,
            price: details.price  // Include price in the request body
          }
        })
      });

      if (handleUnauthorizedResponse(response)) {
        return { success: false, error: 'Unauthorized' };
      }

      const data = await response.json();
      if (data.success) {
        await fetchHomes(); // Refresh list
        return { success: true };
      } else {
        throw new Error(data.error || 'Failed to create home');
      }
    } catch (error) {
      console.error('Error creating home:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create home'
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteHome = async (id: string) => {
    try {
      // Add the home ID to the deleting list
      setDeletingHomeIds(prev => [...prev, id]);
      
      const response = await fetch(`/api/homes/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (handleUnauthorizedResponse(response)) {
        return { success: false, error: 'Unauthorized' };
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete home');
      } else {
        setHomes(prevHomes => prevHomes.filter(home => home.id !== id));
        return { success: true };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete home'
      };
    } finally {
      // Remove the home ID from the deleting list whether successful or not
      setDeletingHomeIds(prev => prev.filter(homeId => homeId !== id));
    }
  };

  const updateHome = async (
    id: string, 
    homeName: string, 
    address: string,
    details: {
      bedrooms: number;
      bathrooms: number;
      sqft: number;
      price: number;
    }
  ) => {
    setIsSubmitting(true);
    try {
      // Create a complete details object with all properties
      const completeDetails = {
        address,
        bedrooms: details.bedrooms,
        bathrooms: details.bathrooms,
        sqft: details.sqft,
        price: details.price
      };
      
      // Stringify the details object
      const detailsJson = JSON.stringify(completeDetails);
      
      // Make the API request
      const response = await fetch(`/api/homes/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name: homeName,
          details: completeDetails // Send the structured details as the address
        })
      });

      if (handleUnauthorizedResponse(response)) {
        return { success: false, error: 'Unauthorized' };
      }

      const data = await response.json();
      if (data.success) {
        // Update local state
        setHomes(prevHomes => 
          prevHomes.map(home => {
            if (home.id === id) {
              return { ...home, name: homeName, details: detailsJson };
            }
            return home;
          })
        );
        return { success: true, updatedDetails: detailsJson };
      } else {
        throw new Error(data.error || 'Failed to update home');
      }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update home'
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    homes,
    isLoading,
    error,
    isSubmitting,
    deletingHomeIds,
    fetchHomes,
    createHome,
    deleteHome,
    updateHome,
    setHomes
  };
}